// src/pages/ChatBot/query-logs/QueriesPanel.tsx
//
// Queries tab of the Manage Chat console. Lists query logs from the
// query-logs admin API in a filterable, paginated table (fixed 50-row
// pages, zero-based) with per-row Inspect / Custom / Triage actions that
// open the matching modals. Filtering lives in the table header: a funnel
// popover on the Status column (single-select) and the Flags column
// (multi-select, OR semantics).

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { fetchQueryLogs } from './api';
import { formatDateTime } from './derive';
import type {
  ProjectedQueryLog,
  QueryFlag,
  QueryFlagName,
  QueryStatus,
  QueryStatusName,
} from './types';
import { FlagsHeaderFilter, StatusHeaderFilter } from './header-filter';
import { statusTone, flagTone } from './chip-tones';
import { EMPTY_SEARCH, parseSearchQuery } from './search-parser';
import type { SearchTerms } from './search-parser';
import Tag from '../shared/Tag';
import InspectModal from './InspectModal';
import CustomAnswerModal from './CustomAnswerModal';
import TriageModal from './TriageModal';
import './QueriesPanel.css';

// Which header funnel popover is open, if any — only one at a time.
type OpenFilter = 'status' | 'flags' | null;

const TABLE_COLUMN_COUNT = 6;

export default function QueriesPanel() {
  const { isAuthenticated } = useAuth();

  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<QueryStatusName | ''>('');
  const [flagFilter, setFlagFilter] = useState<QueryFlagName[]>([]);
  // Staged search text: typing updates this but does NOT refetch — only submit
  // (Enter / the Search button) copies the parsed terms into `searchTerms`,
  // which the fetch effect actually depends on.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerms, setSearchTerms] = useState<SearchTerms>(EMPTY_SEARCH);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState<OpenFilter>(null);
  const [logs, setLogs] = useState<ProjectedQueryLog[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [statusOptions, setStatusOptions] = useState<QueryStatus[]>([]);
  const [flagOptions, setFlagOptions] = useState<QueryFlag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [inspectId, setInspectId] = useState<string | null>(null);
  const [customId, setCustomId] = useState<string | null>(null);
  const [triageLog, setTriageLog] = useState<ProjectedQueryLog | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    // Stale-response guard: filters/page can change (or the panel unmount)
    // while a request is in flight — the cleanup flips this and the late
    // response is dropped instead of calling setState.
    let cancelled = false;

    setIsLoading(true);
    setError('');
    fetchQueryLogs({
      status: statusFilter || undefined,
      flags: flagFilter.length > 0 ? flagFilter : undefined,
      question: searchTerms.question || undefined,
      user: searchTerms.user || undefined,
      timestamp: searchTerms.timestamp || undefined,
      page,
    })
      .then((data) => {
        if (cancelled) return;
        setLogs(data.logs);
        setHasMore(data.hasMore);
        setStatusOptions(Object.values(data.statuses).sort((a, b) => a.id - b.id));
        // 'low-confidence' is derived from the chunk trace, which list rows
        // never carry — it can never appear here and the server cannot
        // filter by it, so it is excluded from the filter options.
        setFlagOptions(
          Object.values(data.flags)
            .filter((f) => f.flag !== 'low-confidence')
            .sort((a, b) => a.id - b.id),
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load query logs.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, page, statusFilter, flagFilter, searchTerms, reloadToken]);

  // Status is single-select: applying a value (or the "All" reset) restarts
  // paging at the first page.
  const handleStatusChange = (next: QueryStatusName | '') => {
    setStatusFilter(next);
    setPage(0);
  };

  // Flags are multi-select with OR semantics: toggling one adds/removes it and
  // restarts paging; the popover stays open so several can be picked in a row.
  const handleFlagToggle = (flag: QueryFlagName) => {
    setFlagFilter((prev) =>
      prev.includes(flag) ? prev.filter((f) => f !== flag) : [...prev, flag],
    );
    setPage(0);
  };

  const handleFlagsClear = () => {
    setFlagFilter([]);
    setPage(0);
  };

  const toggleOpenFilter = (filter: 'status' | 'flags') =>
    setOpenFilter((current) => (current === filter ? null : filter));

  // Close a specific popover without disturbing another that may have opened.
  const closeOpenFilter = (filter: 'status' | 'flags') =>
    setOpenFilter((current) => (current === filter ? null : current));

  // Submitting parses the staged text into terms and restarts paging. A fresh
  // object is always stored so an identical resubmit still refetches.
  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerms(parseSearchQuery(searchInput));
    setPage(0);
  };

  // Clearing empties the input and refetches unfiltered from the first page.
  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerms({ question: '', user: '', timestamp: '' });
    setPage(0);
  };

  // Refetches the current page keeping filters — also the modals' onChanged.
  const refetchCurrentPage = () => setReloadToken((token) => token + 1);

  // isAuthenticated is boolean | null; null means auth is still
  // bootstrapping — render nothing rather than a misleading logged-out
  // message.
  if (isAuthenticated === null) return null;
  if (!isAuthenticated) {
    return (
      <div className="qlog-empty">
        You must be logged in as an admin to view queries.
      </div>
    );
  }

  const hasActiveSearch =
    searchTerms.question !== '' || searchTerms.user !== '' || searchTerms.timestamp !== '';
  const hasActiveFilters = statusFilter !== '' || flagFilter.length > 0 || hasActiveSearch;

  return (
    <div className="qlog-panel">
      {error && <div className="qlog-error">{error}</div>}

      <div className="qlog-toolbar">
        {/* Left cluster: coded search + Create. Typing only stages the text;
            submitting (Enter or the Search button) applies the parsed terms. */}
        <form className="qlog-search" onSubmit={handleSearchSubmit}>
          <div className="qlog-search-field">
            <input
              type="text"
              className={`qlog-search-input${hasActiveSearch ? ' qlog-search-input-active' : ''}`}
              placeholder="Search… (query=, user=, timestamp=)"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              aria-label="Search queries"
            />
            {(searchInput !== '' || hasActiveSearch) && (
              <button
                type="button"
                className="qlog-search-clear"
                title="Clear search"
                aria-label="Clear search"
                onClick={handleSearchClear}
              >
                ✕
              </button>
            )}
          </div>
          <button
            type="submit"
            className="qlog-btn-search"
            title="Search"
            aria-label="Search"
            disabled={isLoading}
          >
            🔍
          </button>
          <button
            type="button"
            className="qlog-btn-create"
            onClick={() => setCreateOpen(true)}
          >
            Create
          </button>
          {hasActiveSearch && <span className="qlog-search-hint">filtered</span>}
        </form>
        <div className="qlog-pager">
          <button
            type="button"
            className="qlog-btn-refresh"
            title="Refresh"
            aria-label="Refresh"
            onClick={refetchCurrentPage}
            disabled={isLoading}
          >
            ↻
          </button>
          <span className="qlog-page-label">Page {page + 1}</span>
          <button
            type="button"
            className="qlog-btn-page"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || isLoading}
          >
            Prev
          </button>
          <button
            type="button"
            className="qlog-btn-page"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore || isLoading}
          >
            Next
          </button>
        </div>
      </div>

      {/* The friendly "nothing here yet" card only shows when there is truly
          nothing to filter. Once a filter is active (or rows exist) the table
          header renders instead, so its funnels stay reachable — including
          across the refetch a Flags toggle triggers, which must not close the
          open popover. */}
      {logs.length === 0 && !isLoading && !hasActiveFilters ? (
        <div className="qlog-empty">No query logs yet.</div>
      ) : (
        <div className="qlog-table-card">
          <table className="qlog-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Query</th>
                <th>
                  <span className="qlog-hf-th">
                    Status
                    <StatusHeaderFilter
                      options={statusOptions}
                      value={statusFilter}
                      onChange={handleStatusChange}
                      isOpen={openFilter === 'status'}
                      onToggle={() => toggleOpenFilter('status')}
                      onClose={() => closeOpenFilter('status')}
                    />
                  </span>
                </th>
                <th>
                  <span className="qlog-hf-th">
                    Flags
                    <FlagsHeaderFilter
                      options={flagOptions}
                      selected={flagFilter}
                      onToggleFlag={handleFlagToggle}
                      onClear={handleFlagsClear}
                      isOpen={openFilter === 'flags'}
                      onToggle={() => toggleOpenFilter('flags')}
                      onClose={() => closeOpenFilter('flags')}
                    />
                  </span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className="qlog-table-status">
                    <span className="qlog-table-status-inner">
                      <span className="qlog-spinner" />
                      Loading query logs…
                    </span>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className="qlog-table-status">
                    No queries match the current filters.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <QueryLogRow
                    key={log.id}
                    log={log}
                    onInspect={() => setInspectId(log.id)}
                    onCustom={() => setCustomId(log.id)}
                    onTriage={() => setTriageLog(log)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <InspectModal queryId={inspectId} onClose={() => setInspectId(null)} />
      <CustomAnswerModal
        queryId={customId}
        onClose={() => setCustomId(null)}
        onChanged={refetchCurrentPage}
      />
      {/* Create-from-scratch: a dedicated instance so edit mode (above) stays
          untouched. onChanged refetches — the new row lists newest-first. */}
      <CustomAnswerModal
        queryId={null}
        createOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onChanged={refetchCurrentPage}
      />
      <TriageModal
        log={triageLog}
        onClose={() => setTriageLog(null)}
        onChanged={refetchCurrentPage}
      />
    </div>
  );
}

interface QueryLogRowProps {
  log: ProjectedQueryLog;
  onInspect: () => void;
  onCustom: () => void;
  onTriage: () => void;
}

function QueryLogRow({ log, onInspect, onCustom, onTriage }: QueryLogRowProps) {
  return (
    <tr>
      <td className="qlog-cell-time">{formatDateTime(log.askedAt)}</td>
      <td>{log.askedBy}</td>
      <td>
        <div className="qlog-question" title={log.question}>
          {log.question}
        </div>
      </td>
      <td>
        <Tag tone={statusTone(log.status.status)}>{log.status.status}</Tag>
      </td>
      <td>
        <div className="qlog-flags">
          {log.flags.map((flag) => (
            <Tag key={flag.flag} tone={flagTone(flag)}>
              {flag.flag}
            </Tag>
          ))}
        </div>
      </td>
      <td>
        <div className="qlog-actions">
          <button
            type="button"
            className="qlog-btn-icon qlog-btn-inspect"
            title="Inspect"
            aria-label="Inspect"
            onClick={onInspect}
          >
            👁
          </button>
          <button
            type="button"
            className="qlog-btn-icon qlog-btn-custom"
            title="Custom"
            aria-label="Custom"
            onClick={onCustom}
          >
            ✎
          </button>
          <button
            type="button"
            className="qlog-btn-icon qlog-btn-triage"
            title="Triage"
            aria-label="Triage"
            onClick={onTriage}
          >
            ⚑
          </button>
        </div>
      </td>
    </tr>
  );
}
